import './qbooksBillFieldsSection.scss';

import type { ExtractComponentProp } from '@approvalmax/types';
import { DateTimeEditor, DropdownEditor, Field, prioritizeAndMapDropdownItems, TextEditor } from '@approvalmax/ui';
import { Text, Tooltip } from '@approvalmax/ui/src/components';
import { useAtomValue } from 'jotai';
import { constants } from 'modules/common';
import { domain } from 'modules/data';
import { PlainDataProvider } from 'modules/data-providers';
import { useShallowEqualSelector } from 'modules/react-redux';
import moment from 'moment';
import { useOcrData } from 'pages/requestForm/hooks/useOcrData';
import { FC, memo, useCallback, useMemo } from 'react';
import bemFactory from 'react-bem-factory';
import { useDispatch } from 'react-redux';
import { ocrIsLoadingState } from 'shared/states';

import {
    changeQbooksDate,
    changeQbooksDepartment,
    changeQbooksDueDate,
    changeQbooksNumber,
    changeQbooksTerms,
} from '../../../../../actions';
import { SectionContainer } from '../../../../../components/SectionContainer';
import { getDepartmentsWithNesting, QBooksDepartment } from '../../../../../data/qbooks/QBooksDepartment';
import { useGetRecognizedOcrAttachment } from '../../../../../hooks/useGetRecognizedOcrAttachment';
import { getRequest, getShowValidationErrors } from '../../../../../selectors/pageSelectors';
import { getIsRequiredLocation, getQBooksContext } from '../../../../../selectors/qbooks';
import { defaultOnChangeFieldRequiredForMatching } from '../../QBooksBillForm.helpers';
import type { OnChangeFieldRequiredForMatching } from '../../QBooksBillForm.types';
import { messages } from './QBooksBillFieldsSection.messages';

const bem = bemFactory.block('reqf-qbooks-bill-fields-section');
const qa = bemFactory.qa('reqf-qbooks-bill-fields-section');

interface QBooksBillFieldsSectionProps {
    onChange?: OnChangeFieldRequiredForMatching;
}

export const QBooksBillFieldsSection: FC<QBooksBillFieldsSectionProps> = memo((props) => {
    const { onChange = defaultOnChangeFieldRequiredForMatching } = props;

    const dispatch = useDispatch();
    const {
        qbooksContext,
        department,
        billNumber: number,
        billDate: date,
        dueDate,
        terms,
        showValidationErrors,
        isRequiredLocation,
        request,
    } = useShallowEqualSelector((state) => {
        const request = getRequest<domain.QBooksBillRequest>(state);
        const details = request.details;

        return {
            qbooksContext: getQBooksContext(state),
            billNumber: details.number,
            billDate: details.date,
            dueDate: details.dueDate,
            terms: details.term,
            department: details.department,
            showValidationErrors: getShowValidationErrors(state),
            isRequiredLocation: getIsRequiredLocation(state, request),
            request,
        };
    });
    const ocrIsLoading = useAtomValue(ocrIsLoadingState);

    const ocrAttachment = useGetRecognizedOcrAttachment(request);

    const { data: ocrData } = useOcrData(request, ocrAttachment?.id);

    const showDueDateWarning = ocrAttachment && moment(dueDate).isBefore(date);

    const showDepartmentWarning = ocrData?.document?.departmentIds && ocrData.document?.departmentIds.length > 1;

    const departments = useMemo(() => {
        if (ocrAttachment && ocrData) {
            return prioritizeAndMapDropdownItems(qbooksContext.departments, ocrData?.document?.departmentIds);
        }

        return getDepartmentsWithNesting(qbooksContext.departments);
    }, [qbooksContext.departments, ocrAttachment, ocrData]);

    const handleBillDateChange = useCallback<ExtractComponentProp<typeof DateTimeEditor, 'onChange'>>(
        (value) => {
            // it's impossible to receive `null` because of `hideClearButton`, but
            // due to bad typings we still have `null` as a type
            if (!value) {
                // again, this is a dirty-hack because technically, we shouldn't allow to
                // clean the DatePicker, but it is possible to select all input content and
                // clean it via backspace
                dispatch(changeQbooksDate(''));
                queueMicrotask(() => {
                    dispatch(changeQbooksDate(date));
                });

                return;
            }

            if (moment(value).isBefore(moment(date))) {
                onChange(() => {
                    dispatch(changeQbooksDate(value));
                }).catch(() => {
                    // this is a dirty-hack because of `DateTimeEditor` is not really controllable
                    // it listens value change and sets internal input state on that.
                    // That's why we have to fire action two times, moreover we have to do it with
                    // a bit of delay, because otherwise only second action will have an effect
                    dispatch(changeQbooksDate(value));
                    queueMicrotask(() => {
                        dispatch(changeQbooksDate(date));
                    });
                });
            } else {
                dispatch(changeQbooksDate(value));
            }
        },
        [date, dispatch, onChange]
    );

    return (
        <SectionContainer className={bem()} qa={qa()} title={messages.sectionName}>
            <div className={bem('content')}>
                <div className={bem('row')}>
                    <Field className={bem('col')} title={messages.termsField}>
                        <PlainDataProvider items={qbooksContext.terms}>
                            <DropdownEditor
                                qa={qa('department-editor')}
                                placeholder={messages.termsPlaceholder}
                                disabled={ocrIsLoading}
                                value={terms}
                                onChange={(value: Parameters<typeof changeQbooksTerms>[0]) =>
                                    dispatch(changeQbooksTerms(value))
                                }
                            />
                        </PlainDataProvider>
                    </Field>

                    <Field className={bem('col')} required title={messages.billDateField}>
                        <DateTimeEditor
                            hideClearButton
                            qa={qa('bill-date-editor')}
                            placeholder={messages.billDatePlaceholder}
                            invalid={showValidationErrors && !date}
                            disabled={ocrIsLoading}
                            value={date}
                            onChange={handleBillDateChange}
                        />
                    </Field>

                    <Field className={bem('col')} required title={messages.dueDateField}>
                        <Tooltip
                            disabled={!showDueDateWarning}
                            placement='top'
                            width='max-content'
                            activator={
                                <DateTimeEditor
                                    hideClearButton
                                    warning={showDueDateWarning}
                                    qa={qa('due-date-editor')}
                                    placeholder={messages.dueDatePlaceholder}
                                    invalid={showValidationErrors && !dueDate}
                                    disabled={ocrIsLoading}
                                    value={dueDate}
                                    onChange={(value) => {
                                        value && dispatch(changeQbooksDueDate(value));
                                    }}
                                />
                            }
                        >
                            <Text spacing='8' font='body' fontSize='small'>
                                {messages.earlyDueDateWarning}
                            </Text>
                        </Tooltip>
                    </Field>

                    <div className={bem('col')} />
                </div>

                <div className={bem('row')}>
                    <Field className={bem('col')} title={messages.billNoField}>
                        <TextEditor
                            changeOnBlur
                            qa={qa('bill-number-editor')}
                            disabled={ocrIsLoading}
                            placeholder={messages.billNumberPlaceholder}
                            maxLength={constants.qBooksConstants.BILL_NUMBER_MAX_LENGTH}
                            value={number || ''}
                            onChange={(value) => dispatch(changeQbooksNumber(value))}
                        />
                    </Field>

                    {qbooksContext.hasDepartmentFeature ? (
                        <Field
                            title={qbooksContext.departmentField?.text || ''}
                            className={bem('col')}
                            required={isRequiredLocation}
                        >
                            <Tooltip
                                disabled={!showDepartmentWarning}
                                placement='top'
                                width='max-content'
                                activator={
                                    <PlainDataProvider items={departments}>
                                        <DropdownEditor
                                            qa={qa('department-editor')}
                                            warning={showDepartmentWarning}
                                            invalid={showValidationErrors && isRequiredLocation && !department}
                                            disabled={ocrIsLoading}
                                            placeholder={messages.departmentPlaceholder({
                                                fieldText: qbooksContext.departmentField?.text,
                                            })}
                                            value={department}
                                            onChange={(value: Parameters<typeof changeQbooksDepartment>[0]) =>
                                                dispatch(changeQbooksDepartment(value))
                                            }
                                            listItem={(props) => (
                                                <DropdownEditor.ListItem
                                                    {...props}
                                                    nestingLevel={(props.item as QBooksDepartment).nestingLevel}
                                                />
                                            )}
                                        />
                                    </PlainDataProvider>
                                }
                            >
                                <Text spacing='8' font='body' fontSize='small'>
                                    {messages.multipleDepartmentsWarning({
                                        fieldText: qbooksContext.departmentField?.text.toLocaleLowerCase(),
                                    })}
                                </Text>
                            </Tooltip>
                        </Field>
                    ) : (
                        <div className={bem('col')} />
                    )}

                    <div className={bem('col')} />

                    <div className={bem('col')} />
                </div>
            </div>
        </SectionContainer>
    );
});

QBooksBillFieldsSection.displayName = 'QBooksBillFieldsSection';
